import React from 'react';
import { Mic, MicOff, Volume2, Radio, AlertTriangle, Waves } from 'lucide-react';
import { AVSlider } from './ui/AVSlider';
import { AVButton } from './ui/AVButton';
import { MicChannel } from '../types/crestron';
interface AudioPageProps {
  masterVolume: number;
  zoneTrims: {
    front: number;
    mid: number;
    rear: number;
  };
  mics: MicChannel[];
  onMasterVolumeChange: (val: number) => void;
  onZoneTrimChange: (zone: 'front' | 'mid' | 'rear', val: number) => void;
  onMicGroupToggle: () => void;
  onPanicMute: (type: 'all' | 'handheld') => void;
  onMicLevelChange: (id: string, level: number) => void;
  onMicMuteToggle: (id: string) => void;
}
export function AudioPage({
  masterVolume,
  mics,
  onMasterVolumeChange,
  onMicGroupToggle,
  onPanicMute,
  onMicLevelChange,
  onMicMuteToggle
}: AudioPageProps) {
  const allMicsMuted = mics.every(m => m.muted);
  return <div className="h-full p-4 grid grid-cols-12 gap-4" data-id="element-59">
      {/* Left: Master Volume */}
      <div className="col-span-2 flex flex-col gap-4" data-id="element-60">
        {/* Master Fader */}
        <div className="flex-1 bg-gradient-to-b from-neutral-800 to-neutral-900 rounded-xl p-3 border border-neutral-700 flex flex-col items-center shadow-xl" data-id="element-61">
          <div className="mb-2 flex flex-col items-center gap-0.5" data-id="element-62">
            <Volume2 className="text-green-400" size={20} strokeWidth={2.5} data-id="element-63" />
            <span className="text-white font-bold text-xs uppercase tracking-wider" data-id="element-64">
              Master
            </span>
            <span className="text-[10px] text-gray-500" data-id="element-65">Main Out</span>
          </div>

          <div className="flex-1 w-full flex justify-center py-1" data-id="element-66">
            <AVSlider value={masterVolume} onChange={onMasterVolumeChange} height="h-full" color="green" label="" data-id="element-67" />
          </div>

          {/* Peak Indicator */}
          <div className="mt-2 w-full" data-id="element-68">
            <div className="flex items-center justify-between mb-1" data-id="element-69">
              <span className="text-[10px] text-gray-500" data-id="element-70">Level</span>
              <span className={`text-[10px] font-bold ${masterVolume > 85 ? 'text-red-400' : masterVolume > 70 ? 'text-amber-400' : 'text-green-400'}`} data-id="element-71">
                {masterVolume > 85 ? 'PEAK' : masterVolume > 70 ? 'HIGH' : 'OK'}
              </span>
            </div>
            <div className="flex gap-0.5" data-id="element-72">
              {[...Array(10)].map((_, i) => <div key={i} className={`flex-1 h-1.5 rounded-sm ${i < Math.floor(masterVolume / 10) ? i >= 8 ? 'bg-red-500' : i >= 7 ? 'bg-amber-500' : 'bg-green-500' : 'bg-neutral-700'}`} data-id="element-73" />)}
            </div>
          </div>
        </div>

        {/* Speaker Routing Info */}
        <div className="bg-neutral-800/50 rounded-lg p-3 border border-neutral-700" data-id="element-74">
          <div className="flex items-center gap-1.5 mb-2" data-id="element-75">
            <Waves size={14} className="text-blue-400" data-id="element-76" />
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-77">
              Routing
            </span>
          </div>
          <div className="space-y-1.5 text-[10px]" data-id="element-78">
            <div className="flex justify-between" data-id="element-79">
              <span className="text-gray-500" data-id="element-80">FOH:</span>
              <span className="text-green-400 font-bold" data-id="element-81">ON</span>
            </div>
            <div className="flex justify-between" data-id="element-82">
              <span className="text-gray-500" data-id="element-83">Subs:</span>
              <span className="text-green-400 font-bold" data-id="element-84">ON</span>
            </div>
            <div className="flex justify-between" data-id="element-85">
              <span className="text-gray-500" data-id="element-86">Ceiling:</span>
              <span className="text-green-400 font-bold" data-id="element-87">DELAY</span>
            </div>
            <div className="flex justify-between" data-id="element-88">
              <span className="text-gray-500" data-id="element-89">DSP:</span>
              <span className="text-blue-400 font-bold" data-id="element-90">SPEECH</span>
            </div>
          </div>
        </div>
      </div>

      {/* Center: Individual Microphone Controls */}
      <div className="col-span-7 flex flex-col gap-3" data-id="element-91">
        {/* Mic Group Control Header */}
        <div className="bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-xl p-4 border border-neutral-700 shadow-xl" data-id="element-92">
          <div className="flex items-center justify-between mb-4" data-id="element-93">
            <div data-id="element-94">
              <h2 className="text-base font-bold text-white flex items-center gap-2 mb-0.5" data-id="element-95">
                <Radio className="text-purple-400" size={18} strokeWidth={2.5} data-id="element-96" />
                Wireless Microphones
              </h2>
              <p className="text-[10px] text-gray-500" data-id="element-97">
                Shure MXW Dante • Individual Control
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-98">
              <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-99">
                Status
              </span>
              <div className="flex gap-1.5" data-id="element-100">
                {mics.map(mic => <div key={mic.id} className="relative group" data-id="element-101">
                    <div className={`h-3 w-3 rounded-full border-2 transition-all ${mic.muted ? 'bg-red-500/20 border-red-500' : 'bg-green-500 border-green-400 shadow-[0_0_8px_rgba(34,197,94,0.5)] animate-pulse'}`} data-id="element-102" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10" data-id="element-103">
                      {mic.name}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-104">
            <AVButton variant={!allMicsMuted ? 'active' : 'default'} label="ALL MICS ON" icon={Mic} onClick={onMicGroupToggle} active={!allMicsMuted} size="md" data-id="element-105" />
            <AVButton variant={allMicsMuted ? 'danger' : 'default'} label="MUTE ALL MICS" icon={MicOff} onClick={onMicGroupToggle} active={allMicsMuted} size="md" data-id="element-106" />
          </div>
        </div>

        {/* Individual Mic Faders */}
        <div className="flex-1 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-xl p-4 border border-neutral-700 shadow-xl" data-id="element-107">
          <div className="flex gap-3 h-full" data-id="element-108">
            {mics.map(mic => <div key={mic.id} className="flex-1 bg-neutral-900/80 rounded-lg p-3 border border-neutral-800 flex flex-col items-center gap-2 hover:border-neutral-700 transition-colors" data-id="element-109">
                {/* Mic Type Icon */}
                <div className={`p-1.5 rounded-lg ${mic.muted ? 'bg-red-900/30' : 'bg-green-900/30'}`} data-id="element-110">
                  <Mic size={16} className={mic.muted ? 'text-red-400' : 'text-green-400'} strokeWidth={2} data-id="element-111" />
                </div>

                <div className="text-[10px] font-bold text-gray-300 truncate w-full text-center uppercase tracking-wider" data-id="element-112">
                  {mic.name}
                </div>

                {/* Level Slider */}
                <div className="flex-1 w-full flex justify-center py-1" data-id="element-113">
                  <AVSlider value={mic.level} onChange={v => onMicLevelChange(mic.id, v)} height="h-full" color="blue" showValue={false} data-id="element-114" />
                </div>

                {/* Level Display */}
                <div className="text-[10px] font-mono font-bold text-gray-400 tabular-nums" data-id="element-115">
                  {mic.level}%
                </div>

                {/* Mute Button */}
                <button onClick={() => onMicMuteToggle(mic.id)} className={`
                    w-full py-1.5 rounded-lg font-bold text-[10px] uppercase transition-all
                    ${mic.muted ? 'bg-red-600 text-white shadow-[0_0_12px_rgba(220,38,38,0.4)] border-2 border-red-400' : 'bg-green-600 text-white shadow-[0_0_12px_rgba(34,197,94,0.4)] border-2 border-green-400'}
                  `} data-id="element-116">
                  {mic.muted ? 'MUTED' : 'ACTIVE'}
                </button>
              </div>)}
          </div>
        </div>
      </div>

      {/* Right: Panic Controls */}
      <div className="col-span-3 flex flex-col gap-3" data-id="element-117">
        {/* Panic Section */}
        <div className="flex-1 bg-gradient-to-br from-red-950/30 to-neutral-900 rounded-xl p-4 border-2 border-red-900/40 shadow-xl flex flex-col" data-id="element-118">
          <div className="flex items-center gap-2 mb-3" data-id="element-119">
            <AlertTriangle className="text-red-400" size={20} strokeWidth={2.5} data-id="element-120" />
            <div data-id="element-121">
              <h3 className="text-red-400 font-bold uppercase tracking-widest text-sm" data-id="element-122">
                Emergency
              </h3>
              <p className="text-[10px] text-red-400/60 mt-0.5" data-id="element-123">Instant mute</p>
            </div>
          </div>

          <div className="flex-1 flex flex-col gap-3" data-id="element-124">
            <AVButton variant="danger" label="MUTE ALL" subLabel="Mics + Program" onClick={() => onPanicMute('all')} className="flex-1" data-id="element-125" />
            <AVButton variant="warning" label="MUTE HANDHELDS" subLabel="Keep Podium" onClick={() => onPanicMute('handheld')} className="flex-1" data-id="element-126" />
          </div>
        </div>

        {/* Quick Info */}
        <div className="bg-neutral-800/50 rounded-lg p-3 border border-neutral-700" data-id="element-127">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-128">
            Quick Stats
          </div>
          <div className="space-y-1.5 text-[10px]" data-id="element-129">
            <div className="flex justify-between" data-id="element-130">
              <span className="text-gray-500" data-id="element-131">Active Mics:</span>
              <span className="text-green-400 font-bold" data-id="element-132">
                {mics.filter(m => !m.muted).length}/{mics.length}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-133">
              <span className="text-gray-500" data-id="element-134">Master Vol:</span>
              <span className="text-white font-bold" data-id="element-135">{masterVolume}%</span>
            </div>
            <div className="flex justify-between" data-id="element-136">
              <span className="text-gray-500" data-id="element-137">DSP Status:</span>
              <span className="text-green-400 font-bold" data-id="element-138">ONLINE</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}